import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Megaphone, Clock, SpeakerHigh, CalendarBlank,
  UsersThree, CookingPot, VideoCamera, Warning,
  Info, CheckCircle, CloudSun, Newspaper, LockSimple, X,
  HandWaving, MapPin, Car, Phone, Moon, Sun
} from '@phosphor-icons/react';

import LivingRoomHub from '@/components/LivingRoomHub';
import { apiOrigin } from '@/utils/apiBase';

const API_BASE = apiOrigin();

const TYPE_CONFIG = {
  urgent: { color: '#EF4444', icon: Warning, bg: 'rgba(239,68,68,0.12)' },
  reminder: { color: '#F59E0B', icon: Clock, bg: 'rgba(245,158,11,0.12)' },
  info: { color: '#3B82F6', icon: Info, bg: 'rgba(59,130,246,0.12)' },
  success: { color: '#22C55E', icon: CheckCircle, bg: 'rgba(34,197,94,0.12)' },
};

export default function DisplayPage() {
  const [searchParams] = useSearchParams();
  const room = searchParams.get('room') || 'all';
  const mode = searchParams.get('mode') || 'home';

  if (mode === 'guest') return <GuestDisplay />;
  return <HomeDisplay room={room} />;
}

function GuestDisplay() {
  const [data, setData] = useState(null);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [forceDay, setForceDay] = useState(false);
  const wakeTimer = useRef(null);

  const fetchGuestFeed = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/api/display/guest-feed`);
      const json = await res.json();
      setData(json);
    } catch (e) { console.error('Guest feed error:', e); }
  }, []);

  useEffect(() => {
    fetchGuestFeed();
    const feedInterval = setInterval(fetchGuestFeed, 60000);
    const clockInterval = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => { clearInterval(feedInterval); clearInterval(clockInterval); };
  }, [fetchGuestFeed]);

  // Night mode: 10PM–7AM
  const hour = currentTime.getHours();
  const isNightTime = hour >= 22 || hour < 7;
  const nightMode = isNightTime && !forceDay;

  // Temporarily wake the display when tapped during night mode
  const handleWake = () => {
    if (!nightMode) return;
    setForceDay(true);
    if (wakeTimer.current) clearTimeout(wakeTimer.current);
    wakeTimer.current = setTimeout(() => setForceDay(false), 30000); // 30s peek
  };

  useEffect(() => () => { if (wakeTimer.current) clearTimeout(wakeTimer.current); }, []);

  if (!data) return (
    <div className="fixed inset-0 bg-[#0A0A0A] flex items-center justify-center">
      <div className="w-8 h-8 border-2 border-[#FF6B35] border-t-transparent rounded-full animate-spin" />
    </div>
  );

  const hasWifi = data.wifi?.ssid;

  // === NIGHT / AMBIENT MODE ===
  if (nightMode) {
    return (
      <div data-testid="guest-display-night" className="fixed inset-0 bg-[#050505] text-white select-none cursor-pointer"
        onClick={handleWake}
        style={{ fontFamily: "'Outfit', 'Manrope', sans-serif" }}>

        {/* Ambient glow */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full opacity-[0.03]"
            style={{ background: 'radial-gradient(circle, #FF6B35 0%, transparent 70%)' }} />
        </div>

        <div className="relative z-10 flex flex-col items-center justify-center h-full gap-8">
          {/* Moon icon */}
          <Moon size={36} weight="duotone" className="text-neutral-700" />

          {/* Large Clock */}
          <div className="text-center">
            <p data-testid="night-clock" className="text-8xl font-bold tracking-tight tabular-nums text-neutral-300"
              style={{ textShadow: '0 0 60px rgba(255,107,53,0.08)' }}>
              {currentTime.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
            </p>
            <p className="text-sm text-neutral-600 mt-2">
              {currentTime.toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long' })}
            </p>
          </div>

          {/* Wi-Fi QR */}
          {hasWifi && (
            <div className="flex flex-col items-center gap-3 mt-4" data-testid="night-wifi-qr">
              <div className="rounded-2xl overflow-hidden p-1" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
                <img src={`${API_BASE}/api/display/guest-wifi-qr`} alt="Wi-Fi QR" className="w-32 h-32 opacity-60" />
              </div>
              <div className="text-center">
                <p className="text-[10px] uppercase tracking-widest text-neutral-600 font-semibold">Wi-Fi</p>
                <p className="text-sm text-neutral-500 font-medium">{data.wifi.ssid}</p>
              </div>
            </div>
          )}

          {/* Tap hint */}
          <p className="text-[10px] text-neutral-700 mt-6 tracking-wider uppercase" data-testid="night-tap-hint">
            Tap anywhere to view full details
          </p>
        </div>
      </div>
    );
  }

  // === DAY / FULL MODE ===
  const hasRules = data.house_rules?.length > 0;
  const hasContacts = data.emergency_contacts?.length > 0;
  const hasPlaces = data.nearby_places?.length > 0;
  const hasTransport = data.local_transport?.length > 0;
  const hasMap = !!data.house_map_url;

  return (
    <div data-testid="guest-display-page" className="fixed inset-0 bg-[#0A0A0A] text-white overflow-y-auto select-none"
      style={{ fontFamily: "'Outfit', 'Manrope', sans-serif" }}>

      {/* Top Bar */}
      <div className="sticky top-0 z-20 flex items-center justify-between px-8 py-5"
        style={{ background: 'linear-gradient(180deg, rgba(10,10,10,0.98) 60%, transparent 100%)' }}>
        <div className="flex items-center gap-4">
          <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-[#FF6B35] to-[#E85D2C] flex items-center justify-center">
            <HandWaving size={20} weight="fill" className="text-white" />
          </div>
          <div>
            <h1 className="text-lg font-bold tracking-tight">{data.family_name || 'Welcome'}</h1>
            <p className="text-[11px] text-neutral-500 uppercase tracking-widest">Guest Mode</p>
          </div>
        </div>
        <div className="flex items-center gap-5">
          {isNightTime && forceDay && (
            <button onClick={() => setForceDay(false)} data-testid="guest-dim-btn"
              className="flex items-center gap-2 px-3 py-1.5 rounded-full text-[10px] font-semibold uppercase tracking-wider transition-colors"
              style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)', color: '#9CA3AF' }}>
              <Moon size={12} /> Dim
            </button>
          )}
          <div className="text-right">
            <p className="text-3xl font-bold tracking-tight tabular-nums">
              {currentTime.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
            </p>
            <p className="text-[11px] text-neutral-500">
              {currentTime.toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long' })}
            </p>
          </div>
        </div>
      </div>

      <div className="px-8 pb-10 space-y-6">
        {/* Welcome Hero */}
        <div className="rounded-3xl p-10 text-center" data-testid="guest-welcome-hero"
          style={{ background: 'linear-gradient(135deg, rgba(255,107,53,0.08) 0%, rgba(255,107,53,0.02) 100%)', border: '1px solid rgba(255,107,53,0.15)' }}>
          <HandWaving size={48} weight="duotone" className="text-[#FF6B35] mx-auto mb-4" />
          <h2 className="text-4xl font-bold tracking-tight mb-2">{data.welcome_message}</h2>
          <p className="text-neutral-400 text-lg">Make yourself at home</p>
          {data.weather && (
            <div className="mt-4 inline-flex items-center gap-3 px-5 py-2.5 rounded-full" style={{ background: 'rgba(255,255,255,0.05)' }}>
              <CloudSun size={20} weight="duotone" className="text-[#F59E0B]" />
              <span className="text-sm text-neutral-300">{data.weather.city} — {data.weather.temp}°C, {data.weather.description}</span>
            </div>
          )}
        </div>

        {/* Main Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

          {/* Left Column - Wi-Fi + Rules */}
          <div className="lg:col-span-2 space-y-6">
            {/* Wi-Fi Card */}
            {hasWifi && (
              <div className="rounded-3xl p-6" data-testid="guest-wifi-card"
                style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-8 h-8 rounded-xl flex items-center justify-center" style={{ background: 'rgba(34,197,94,0.15)' }}>
                    <Info size={16} className="text-[#22C55E]" />
                  </div>
                  <span className="text-[10px] uppercase tracking-widest text-neutral-500 font-semibold">Wi-Fi Access</span>
                </div>
                <div className="flex items-center gap-6">
                  <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <p className="text-[10px] uppercase tracking-widest text-neutral-500 mb-1">Network Name</p>
                      <p className="text-2xl font-bold text-white tracking-tight" data-testid="guest-wifi-ssid">{data.wifi.ssid}</p>
                    </div>
                    <div>
                      <p className="text-[10px] uppercase tracking-widest text-neutral-500 mb-1">Password</p>
                      <p className="text-2xl font-bold text-[#FF6B35] tracking-tight font-mono" data-testid="guest-wifi-password">{data.wifi.password}</p>
                    </div>
                  </div>
                  <div className="flex-shrink-0 text-center" data-testid="guest-wifi-qr">
                    <div className="rounded-2xl overflow-hidden bg-[#0A0A0A] p-1" style={{ border: '1px solid rgba(255,255,255,0.1)' }}>
                      <img src={`${API_BASE}/api/display/guest-wifi-qr`} alt="Wi-Fi QR" className="w-28 h-28" />
                    </div>
                    <p className="text-[9px] text-neutral-500 mt-1.5 font-medium">Scan to connect</p>
                  </div>
                </div>
              </div>
            )}

            {/* House Rules */}
            {hasRules && (
              <div className="rounded-3xl p-6" data-testid="guest-house-rules"
                style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-8 h-8 rounded-xl flex items-center justify-center" style={{ background: 'rgba(59,130,246,0.15)' }}>
                    <Info size={16} className="text-[#3B82F6]" />
                  </div>
                  <span className="text-[10px] uppercase tracking-widest text-neutral-500 font-semibold">House Rules</span>
                </div>
                <div className="space-y-3">
                  {data.house_rules.map((rule, i) => (
                    <div key={i} className="flex items-start gap-3">
                      <span className="w-7 h-7 rounded-lg flex items-center justify-center text-xs font-bold flex-shrink-0"
                        style={{ background: 'rgba(255,107,53,0.12)', color: '#FF6B35' }}>{i + 1}</span>
                      <p className="text-sm text-neutral-300 pt-1">{rule}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Nearby Places */}
            {hasPlaces && (
              <div className="rounded-3xl p-6" data-testid="guest-nearby-places"
                style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-8 h-8 rounded-xl flex items-center justify-center" style={{ background: 'rgba(34,197,94,0.15)' }}>
                    <MapPin size={16} className="text-[#22C55E]" />
                  </div>
                  <span className="text-[10px] uppercase tracking-widest text-neutral-500 font-semibold">Nearby Places</span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {data.nearby_places.map((place, i) => (
                    <div key={i} className="rounded-xl p-4 flex items-center gap-3"
                      style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.04)' }}>
                      <MapPin size={18} className="text-[#22C55E] flex-shrink-0" />
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-white truncate">{place.name}</p>
                        <p className="text-[10px] text-neutral-500">{place.type}</p>
                      </div>
                      {place.distance && (
                        <span className="text-[10px] font-semibold text-neutral-400 px-2 py-1 rounded-lg"
                          style={{ background: 'rgba(255,255,255,0.05)' }}>{place.distance}</span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Local Transport */}
            {hasTransport && (
              <div className="rounded-3xl p-6" data-testid="guest-local-transport"
                style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-8 h-8 rounded-xl flex items-center justify-center" style={{ background: 'rgba(59,130,246,0.15)' }}>
                    <Car size={16} className="text-[#3B82F6]" />
                  </div>
                  <span className="text-[10px] uppercase tracking-widest text-neutral-500 font-semibold">Local Transport</span>
                </div>
                <div className="space-y-3">
                  {data.local_transport.map((tr, i) => (
                    <div key={i} className="rounded-xl p-4 flex items-center gap-4"
                      style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.04)' }}>
                      <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                        style={{ background: 'rgba(59,130,246,0.1)' }}>
                        <Car size={18} className="text-[#3B82F6]" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-white">{tr.type}</p>
                        <p className="text-xs text-neutral-400">{tr.details}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Right Column - Contacts + Map */}
          <div className="space-y-6">
            {/* Emergency Contacts */}
            {hasContacts && (
              <div className="rounded-3xl p-6" data-testid="guest-emergency-contacts"
                style={{ background: 'rgba(239,68,68,0.04)', border: '1px solid rgba(239,68,68,0.12)' }}>
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-8 h-8 rounded-xl flex items-center justify-center" style={{ background: 'rgba(239,68,68,0.15)' }}>
                    <Phone size={16} className="text-red-400" />
                  </div>
                  <span className="text-[10px] uppercase tracking-widest text-neutral-500 font-semibold">Emergency Contacts</span>
                </div>
                <div className="space-y-3">
                  {data.emergency_contacts.map((c, i) => (
                    <div key={i} className="rounded-xl p-3" style={{ background: 'rgba(255,255,255,0.03)' }}>
                      <p className="text-sm font-semibold text-white">{c.name}</p>
                      <p className="text-lg font-bold text-red-400 font-mono tracking-wide">{c.phone}</p>
                      {c.relation && <p className="text-[10px] text-neutral-500 uppercase tracking-wider mt-0.5">{c.relation}</p>}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* House Map */}
            {hasMap && (
              <div className="rounded-3xl overflow-hidden" data-testid="guest-house-map"
                style={{ border: '1px solid rgba(255,255,255,0.06)' }}>
                <div className="p-4 flex items-center gap-2" style={{ background: 'rgba(255,255,255,0.03)' }}>
                  <MapPin size={14} className="text-[#FF6B35]" />
                  <span className="text-[10px] uppercase tracking-widest text-neutral-500 font-semibold">House Map</span>
                </div>
                <img src={data.house_map_url} alt="House Map" className="w-full object-contain max-h-[400px]" />
              </div>
            )}

            {/* If nothing configured in right column */}
            {!hasContacts && !hasMap && (
              <div className="rounded-3xl p-8 text-center" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.04)' }}>
                <HandWaving size={32} className="text-neutral-600 mx-auto mb-2" />
                <p className="text-sm text-neutral-500">Enjoy your stay!</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function HomeDisplay({ room }) {
  const [data, setData] = useState(null);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [newsHeadlines, setNewsHeadlines] = useState([]);
  const ttsQueue = useRef([]);
  const isSpeaking = useRef(false);
  const lastSpokenIds = useRef(new Set());

  // Kiosk mode state
  const [kioskLocked, setKioskLocked] = useState(false);
  const [showPinModal, setShowPinModal] = useState(false);
  const [pinInput, setPinInput] = useState('');
  const [pinError, setPinError] = useState('');
  const [kioskPin, setKioskPin] = useState('');
  const pinInputRef = useRef(null);

  // Fetch display feed
  const fetchFeed = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/api/display/feed?room=${encodeURIComponent(room)}`);
      const json = await res.json();
      setData(json);

      // Load kiosk PIN from settings
      if (json.kiosk_pin) {
        setKioskPin(json.kiosk_pin);
        setKioskLocked(true);
      } else {
        setKioskLocked(false);
      }

      // Queue TTS for new announcements
      if (json.announcements) {
        json.announcements.forEach(ann => {
          if (ann.tts_enabled && !lastSpokenIds.current.has(ann.announcement_id)) {
            lastSpokenIds.current.add(ann.announcement_id);
            ttsQueue.current.push(`${ann.title}. ${ann.message}`);
          }
        });
        processTTS();
      }
    } catch (e) { console.error('Display feed error:', e); }
  }, [room]);

  // Process TTS queue using browser speech
  const processTTS = useCallback(() => {
    if (isSpeaking.current || ttsQueue.current.length === 0) return;
    if (!('speechSynthesis' in window)) return;

    isSpeaking.current = true;
    const text = ttsQueue.current.shift();
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.rate = 0.9;
    utterance.pitch = 1;
    utterance.volume = 1;
    utterance.lang = 'en-IN';
    utterance.onend = () => {
      isSpeaking.current = false;
      processTTS();
    };
    utterance.onerror = () => {
      isSpeaking.current = false;
      processTTS();
    };
    window.speechSynthesis.speak(utterance);
  }, []);

  // Fetch news headlines
  const fetchNews = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/api/display/news`);
      const json = await res.json();
      if (json.headlines?.length) setNewsHeadlines(json.headlines);
    } catch (e) { console.error('News fetch error:', e); }
  }, []);

  useEffect(() => {
    fetchFeed();
    fetchNews();
    const feedInterval = setInterval(fetchFeed, 30000);
    const newsInterval = setInterval(fetchNews, 300000); // 5 min
    const clockInterval = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => { clearInterval(feedInterval); clearInterval(newsInterval); clearInterval(clockInterval); };
  }, [fetchFeed, fetchNews]);

  // Auto-rotate slides
  // (Living Room Hub renders all sections at once — no slide rotation needed)

  // Block keyboard shortcuts when kiosk locked
  useEffect(() => {
    if (!kioskLocked) return;
    const handleKeyDown = (e) => {
      // Block common exit shortcuts
      if (e.key === 'Escape' || e.key === 'F11' ||
          (e.ctrlKey && (e.key === 'w' || e.key === 'l' || e.key === 't' || e.key === 'n')) ||
          (e.altKey && (e.key === 'F4' || e.key === 'Tab'))) {
        e.preventDefault();
        e.stopPropagation();
        setShowPinModal(true);
      }
    };
    const handleContextMenu = (e) => { e.preventDefault(); };
    window.addEventListener('keydown', handleKeyDown, true);
    window.addEventListener('contextmenu', handleContextMenu, true);
    return () => {
      window.removeEventListener('keydown', handleKeyDown, true);
      window.removeEventListener('contextmenu', handleContextMenu, true);
    };
  }, [kioskLocked]);

  // Focus pin input when modal opens
  useEffect(() => {
    if (showPinModal && pinInputRef.current) {
      setTimeout(() => pinInputRef.current?.focus(), 100);
    }
  }, [showPinModal]);

  const handlePinSubmit = () => {
    if (pinInput === kioskPin) {
      setKioskLocked(false);
      setShowPinModal(false);
      setPinInput('');
      setPinError('');
    } else {
      setPinError('Incorrect PIN');
      setPinInput('');
    }
  };

  const urgentAnnouncements = data?.announcements?.filter(a => a.priority === 'critical' || a.type === 'urgent') || [];

  return (
    <>
      <LivingRoomHub
        data={data}
        currentTime={currentTime}
        newsHeadlines={newsHeadlines}
        kioskLocked={kioskLocked && !showPinModal}
        onUnlockTap={() => setShowPinModal(true)}
      />

      {/* Urgent banner overlays the hub when critical alerts exist */}
      {urgentAnnouncements.length > 0 && (
        <div className="fixed top-4 left-1/2 -translate-x-1/2 z-30 max-w-3xl w-[90%]" data-testid="urgent-banner">
          <div className="rounded-2xl overflow-hidden" style={{ background: 'rgba(239,68,68,0.18)', border: '1px solid rgba(239,68,68,0.4)', backdropFilter: 'blur(20px)' }}>
            <div className="flex items-center gap-4 px-6 py-3 animate-pulse">
              <Warning size={24} weight="fill" className="text-red-400 flex-shrink-0" />
              <div className="flex-1 overflow-hidden">
                <div className="flex items-center gap-3">
                  {urgentAnnouncements.slice(0, 1).map(a => (
                    <span key={a.announcement_id} className="text-red-200 font-semibold text-sm whitespace-nowrap overflow-hidden text-ellipsis">
                      {a.title} — {a.message}
                    </span>
                  ))}
                </div>
              </div>
              <SpeakerHigh size={18} className="text-red-400 flex-shrink-0" />
            </div>
          </div>
        </div>
      )}

      {/* PIN Entry Modal */}
      {showPinModal && (
        <div data-testid="kiosk-pin-modal" className="fixed inset-0 z-[60] flex items-center justify-center"
          style={{ background: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(12px)' }}>
          <div className="w-full max-w-sm mx-4 rounded-3xl p-8 text-center"
            style={{ background: 'rgba(30,30,30,0.95)', border: '1px solid rgba(255,255,255,0.1)' }}>
            <div className="w-16 h-16 rounded-2xl mx-auto mb-5 flex items-center justify-center"
              style={{ background: 'rgba(255,107,53,0.15)' }}>
              <LockSimple size={32} weight="duotone" className="text-[#FF6B35]" />
            </div>
            <h2 className="text-xl font-bold text-white mb-1">Kiosk Locked</h2>
            <p className="text-sm text-neutral-400 mb-6">Enter PIN to unlock the display</p>

            <div className="mb-4">
              <input
                ref={pinInputRef}
                type="password"
                inputMode="numeric"
                pattern="[0-9]*"
                value={pinInput}
                onChange={e => { setPinInput(e.target.value); setPinError(''); }}
                onKeyDown={e => { if (e.key === 'Enter') handlePinSubmit(); }}
                data-testid="kiosk-pin-input"
                placeholder="• • • •"
                className="w-full px-4 py-3 text-center text-2xl tracking-[0.5em] rounded-xl bg-black/40 text-white outline-none focus:ring-2"
                style={{ border: `1px solid ${pinError ? '#EF4444' : 'rgba(255,255,255,0.1)'}`, '--tw-ring-color': '#FF6B35' }}
              />
              {pinError && <p className="text-red-400 text-xs mt-2">{pinError}</p>}
            </div>

            <div className="flex gap-2">
              <button onClick={() => { setShowPinModal(false); setPinInput(''); setPinError(''); }}
                className="flex-1 py-3 rounded-xl bg-white/5 text-neutral-400 text-sm font-medium hover:bg-white/10">
                Cancel
              </button>
              <button onClick={handlePinSubmit} data-testid="kiosk-pin-submit"
                className="flex-1 py-3 rounded-xl text-white text-sm font-semibold"
                style={{ background: 'linear-gradient(135deg, #FF6B35, #E85D2C)' }}>
                Unlock
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

